import { useLocation, useNavigate } from 'react-router-dom';
import { AccountantMenu } from '@/pages/roles/accountant/AccountantMenu';
import { BurgerMenu } from '@/components/kit';
import { useShellIdentity } from '@/components/shell/useShellIdentity';
import { useAuthStore } from '@/stores/useAuthStore';
import { useFeedbackStore } from '@/stores/useFeedbackStore';
import { useAlerts } from '../lib/useManagerData';

/** Which door the screen opening the menu IS, so it can wear "Home". */
export type ManagerDoor = 'today' | 'alerts' | 'reports' | 'audit' | 'settings';

/**
 * THE CLINIC MANAGER'S ☰ — `maie-phone-clinic-manager/g_c0`.
 *
 * FIVE DOORS, AND A PATIENT IS NOT ONE. The board's own ghost card says it:
 * "Patients never appear in this menu, and never will — the manager's surfaces
 * are aggregates." Every door here opens a total, a list of totals, or the
 * settings that produce them; a manager who needs one person's chart is a
 * doctor at that moment and reaches it through the doctor's surface.
 *
 * THE LEDGER AND THE DIGEST ARE NOT DOORS EITHER. Both are extensions of the
 * day — Today's branch row opens the ledger, Today's digest card opens the
 * digest — so Today owns them, which is also why they are nested under it in
 * the route table.
 */
/**
 * The report screens are shared with the accountant (`/accountant/reports/*`
 * mounts these same pages), so ☰ follows the lane in the URL — an accountant
 * on Revenue opens the books, not the manager's doors.
 */
export function ManagerMenu(props: {
  door: ManagerDoor;
  onClose: () => void;
  counts?: Partial<Record<ManagerDoor, string>>;
}) {
  const { pathname } = useLocation();
  if (pathname.startsWith('/accountant')) {
    return <AccountantMenu door={props.door === 'reports' ? 'reports' : 'home'} onClose={props.onClose} />;
  }
  return <ManagerLaneMenu {...props} />;
}

function ManagerLaneMenu({
  door,
  onClose,
  counts,
}: {
  door: ManagerDoor;
  onClose: () => void;
  counts?: Partial<Record<ManagerDoor, string>>;
}) {
  const navigate = useNavigate();
  const { person, branch } = useShellIdentity();
  const logout = useAuthStore((s) => s.logout);
  const openFeedback = useFeedbackStore((s) => s.setOpen);
  const alerts = useAlerts();
  const waiting = alerts.page.rows.filter(
    (a) => !a.acknowledgedAt && (a.requiresAcknowledgment || !a.readAt)).length;

  const go = (to: string) => {
    onClose();
    navigate(to);
  };
  const home = <span className="chip chip-brand chip-sm">Home</span>;
  const at = (k: ManagerDoor) => (door === k ? home : undefined);

  return (
    <BurgerMenu
      open
      onClose={onClose}
      roleChip="Clinic manager"
      doors={[
        { key: 'today', icon: 'home', label: 'Today',
          sub: counts?.today ?? 'the day, branch by branch', badge: at('today'),
          onSelect: () => go('/clinic-manager/today') },
        { key: 'alerts', icon: 'bell', label: 'Alerts & approvals',
          sub: counts?.alerts ?? (waiting ? `${waiting} waiting on you` : 'what is waiting on you'),
          badge: door === 'alerts' ? home : waiting ? <span className="chip chip-warn chip-sm">{waiting}</span> : undefined,
          onSelect: () => go('/clinic-manager/alerts') },
        { key: 'reports', icon: 'chart-bar', label: 'Reports',
          sub: counts?.reports ?? 'the month, against the one before', badge: at('reports'),
          onSelect: () => go('/clinic-manager/reports') },
        { key: 'audit', icon: 'list-check', label: 'Audit log',
          sub: counts?.audit ?? 'who did what, when', badge: at('audit'),
          onSelect: () => go('/clinic-manager/audit') },
        { key: 'settings', icon: 'settings', label: 'System Settings',
          sub: counts?.settings ?? 'facility · roles · money · wallet · modules · directory · flows',
          badge: at('settings'),
          onSelect: () => go('/clinic-manager/settings') },
      ]}
      note={<>Patients never appear here — every door opens an aggregate, not a person.</>}
      account={{
        name: person?.name ?? 'This account',
        initials: person?.initials ?? 'CM',
        caption: 'account — devices · notifications',
        onSelect: () => go('/settings'),
      }}
      branch={branch}
      onAppearance={() => go('/settings#appearance')}
      onFeedback={() => { onClose(); openFeedback(true); }}
      onSignOut={() => void logout()}
    />
  );
}
